/**
 * Transportfreigabe – Route /approvals (Admin-only).
 *
 * Baut auf der Bordbuch-Optik der alten Vier-Augen-Seite auf: Kopf mit
 * Brotkrume, Becken-Leiste, Tages-Kapitel in einer Liste, Detail-Sheet mit
 * Frachtbrief. Inhalte kommen aus der CI/CD-Welt:
 *   Becken „Offen"       = /api/cicd/release-candidate (Kandidaten je Paket)
 *   Becken „Ausgeführt"  = /api/cicd/release-history   (bereits freigegebene)
 *
 * Die Freigabe geht als POST /api/cicd/transport/release mit approvalNote.
 * Sammelstufe → Ziel (PROD) kommt aus project.stageEnvMap.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api, hatCicdZugang, cicdSpacesVon } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";

// Sprachabhängiges Datumsformat: die Seite ist ein Nachweis, ein falsch
// gelesener Zeitstempel wäre hier kein Schönheitsfehler.
const gebiet = () => (i18n.language === "en" ? "en-GB" : "de-DE");
const datumLang = (d) => d.toLocaleDateString(gebiet(), { day: "numeric", month: "long", year: "numeric" });

function dayKey(iso) {
  const d = new Date(iso);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
function fmtDay(key, t) {
  const d = new Date(key + "T12:00:00");
  const today = dayKey(new Date().toISOString());
  const yest = dayKey(new Date(Date.now() - 864e5).toISOString());
  const datum = datumLang(d);
  if (key === today) return t("schleuse:datum.heute", { datum });
  if (key === yest) return t("schleuse:datum.gestern", { datum });
  return t("schleuse:datum.wochentag", { wochentag: d.toLocaleDateString(gebiet(), { weekday: "long" }), datum });
}
function fmtClock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(gebiet(), { hour: "2-digit", minute: "2-digit" });
}
function fmtFull(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(gebiet(), { day: "2-digit", month: "2-digit", year: "2-digit" }) + ", "
    + d.toLocaleTimeString(gebiet(), { hour: "2-digit", minute: "2-digit" });
}

const HIST_LIMIT_START = 50;

export default function TransportApprovals({ user, spaces = [] }) {
  const { t } = useTranslation(["schleuse", "freigabe", "gemeinsam"]);
  const { toasts, addToast } = useToast();
  // Auch Space-Admins (Eigentuemer/Freigabe role admin) duerfen freigeben - das Backend
  // prueft dieselbe Regel je Space.
  const isAdmin = hatCicdZugang(user, spaces);
  const waehlbareSpaces = useMemo(() => cicdSpacesVon(user, spaces), [user, spaces]);

  const [spaceId, setSpaceId] = useState(() => cicdSpacesVon(user, spaces)[0]?.id || "");
  const [basin, setBasin] = useState("offen");

  const [candData, setCandData] = useState(null);
  const [candLoading, setCandLoading] = useState(false);

  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histLimit, setHistLimit] = useState(HIST_LIMIT_START);

  const [detail, setDetail] = useState(null); // { kind:"candidate"|"history", data:... }
  const [grund, setGrund] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [result, setResult] = useState(null);

  const escRef = useRef({});
  escRef.current = { detail };

  // Wie im CI/CD Monitor: beim Direktaufruf sind die Spaces noch nicht da,
  // spaces[0] im useState greift ins Leere. Nachziehen, sobald sie eintreffen.
  useEffect(() => {
    if (!spaceId && waehlbareSpaces.length > 0) setSpaceId(waehlbareSpaces[0].id);
  }, [waehlbareSpaces, spaceId]);

  const ladeKandidat = useCallback(async (sid) => {
    if (!sid) { setCandData(null); return; }
    setCandLoading(true);
    try { setCandData(await api(`/api/cicd/release-candidate?spaceId=${encodeURIComponent(sid)}`)); }
    catch (e) { addToast(e?.message || i18n.t("freigabe:meldung.kandidatFehler"), "error"); setCandData(null); }
    setCandLoading(false);
  }, [addToast]);

  const ladeHistorie = useCallback(async (sid, limit) => {
    if (!sid) { setHistData(null); return; }
    setHistLoading(true);
    try {
      setHistData(await api(`/api/cicd/release-history?spaceId=${encodeURIComponent(sid)}&limit=${limit}`));
    } catch (e) {
      addToast(e?.message || i18n.t("freigabe:meldung.historieFehler"), "error");
      setHistData(null);
    }
    setHistLoading(false);
  }, [addToast]);

  useEffect(() => {
    if (!isAdmin) return;
    setResult(null); setDetail(null); setGrund(""); setHistLimit(HIST_LIMIT_START);
    ladeKandidat(spaceId);
    ladeHistorie(spaceId, HIST_LIMIT_START);
  }, [isAdmin, spaceId, ladeKandidat, ladeHistorie]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && escRef.current.detail) closeSheet(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => {
    document.body.style.overflow = detail ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [detail]);

  const closeSheet = () => { setDetail(null); setGrund(""); setResult(null); };

  const project = candData?.project || histData?.project || null;
  const prodEnv = project?.stageEnvMap?.prod || "PROD";
  const quellStufe = (candData?.candidate?.sammelStufe || "").toUpperCase()
    || (project?.stageEnvMap?.qas ? project.stageEnvMap.qas : "QAS");

  const candidate = candData?.candidate || null;
  const kandidaten = candidate?.kandidaten || [];

  const histItems = Array.isArray(histData?.items) ? histData.items : [];
  const histTotal = histData?.total || histItems.length;

  const configured = !!candData?.configured;
  const tokenSet = candData?.tokenSet !== false;
  const kandidatVorhanden = kandidaten.length > 0;

  // Zählwerte für die Becken-Leiste (nur ehrliche Zahlen, keine Rateraten).
  const offenCount = kandidaten.length;
  const ausgefuehrtCount = histTotal;

  // Öffnet den Detail-Sheet. Bei Historie erhalten wir alle Felder aus der
  // Antwort; beim Kandidaten setzen wir Ergebnis/Grund zurück.
  const openCandidate = (kand) => {
    setResult(null); setGrund("");
    setDetail({ kind: "candidate", data: kand });
  };
  const openHistory = (item) => {
    setResult(null); setGrund("");
    setDetail({ kind: "history", data: item });
  };

  const freigeben = async () => {
    const payload = detail?.data?.releasePayload;
    if (!payload) { addToast(t("freigabe:meldung.kandidatFehler"), "error"); return; }
    if (grund.trim().length < 3) { addToast(t("freigabe:meldung.grundFehlt"), "error"); return; }
    setReleasing(true); setResult(null);
    try {
      const r = await api("/api/cicd/transport/release", {
        method: "POST",
        body: JSON.stringify({ spaceId, ...payload, approvalNote: grund.trim() }),
      });
      setResult({ ok: true, tag: r?.tag, runUrl: r?.run?.html_url || null });
      addToast(t("freigabe:meldung.freigegeben", { ziel: prodEnv }), "success");
      // Nach der Freigabe: Kandidat und Historie frisch laden.
      ladeKandidat(spaceId);
      ladeHistorie(spaceId, histLimit);
    } catch (e) {
      setResult({ ok: false, error: e?.message || "" });
      addToast(e?.message || t("freigabe:meldung.fehlgeschlagen"), "error");
    }
    setReleasing(false);
  };

  // Historie nach Tag gruppieren (wie in der alten Liste).
  const histChapters = useMemo(() => {
    const map = new Map();
    for (const it of histItems) {
      if (!it.at) continue;
      const k = dayKey(it.at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries());
  }, [histItems]);

  if (user && !isAdmin) {
    return (
      <div className="iio-slz">
        <ToastContainer toasts={toasts} />
        <div className="iio-slz-empty" style={{ maxWidth: 440, margin: "80px auto" }}>
          <h3>{t("freigabe:zugriff.titel")}</h3>
          <p>{t("freigabe:zugriff.text")}</p>
        </div>
      </div>
    );
  }

  // Detail-Ableitung: d zeigt auf den geöffneten Kandidaten oder Historie-Eintrag.
  const d = detail?.data;
  const detailIsCand = detail?.kind === "candidate";
  const detailIsHist = detail?.kind === "history";

  const detailPaket = d?.packageName || d?.packageId || t("schleuse:blatt.keinPaket");

  const detailArtefakte = detailIsHist
    ? (Array.isArray(d?.artifactIds) ? d.artifactIds : [])
    : (d?.items || []).map(x => x.iflow);

  const detailPushes = detailIsHist ? null : (d?.summary?.pushes || 0);
  const detailQuelle = detailIsHist ? (d?.sourceEnv || quellStufe) : quellStufe;
  const detailZiel = detailIsHist ? (d?.targetEnv || prodEnv) : prodEnv;
  const detailPartner = detailIsHist && Array.isArray(d?.partnerIds) ? d.partnerIds : [];

  return (
    <div className="iio-slz">
      <ToastContainer toasts={toasts} />

      {/* Kopf */}
      <div className="iio-page-head">
        <div className="iio-page-head-left iio-anim-arrival">
          <div className="iio-breadcrumb">
            <span className="iio-accent">{t("freigabe:kopf.brotkrume")}</span>
            <span className="iio-sep">/</span>
            <span>{t("freigabe:kopf.brotkrumeAktuell")}</span>
          </div>
          <h1>{t("freigabe:kopf.titel")}</h1>
          <p className="iio-lead">{t("freigabe:kopf.einleitung", { ziel: prodEnv })}</p>
        </div>
      </div>

      {/* Space-Wähler (Leiste). Bewusst schmucklos: eine Zeile, ein Zweck. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 12px", flexWrap: "wrap" }}>
        <select
          value={spaceId}
          onChange={e => setSpaceId(e.target.value)}
          style={{
            height: 36, padding: "0 30px 0 12px", border: "1px solid var(--iio-border)",
            borderRadius: "var(--iio-r-sm)", background: "var(--iio-bg-1)",
            fontSize: 13.5, color: "var(--iio-text-1)", appearance: "auto", outline: "none",
            minWidth: 240, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <option value="">{t("freigabe:leiste.spaceWaehlen")}</option>
          {waehlbareSpaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
        </select>
      </div>

      {/* Becken: nur Offen und Ausgeführt (CI/CD kennt kein Eingeplant/Abgelehnt) */}
      <div className="iio-slz-basins">
        <button type="button" className={"iio-slz-basin" + (basin === "offen" ? " on" : "")} onClick={() => setBasin("offen")}>
          <div className="b-lbl">{t("schleuse:becken.offen")}</div>
          <div className="b-val">{offenCount}</div>
          <div className="b-sub">
            {offenCount > 0 ? t("schleuse:becken.wartetFreigabe", { count: offenCount }) : t("schleuse:becken.nichtsOffen")}
          </div>
        </button>
        <button type="button" className={"iio-slz-basin" + (basin === "ausgefuehrt" ? " on" : "")} onClick={() => setBasin("ausgefuehrt")}>
          <div className="b-lbl">{t("schleuse:becken.ausgefuehrt")}</div>
          <div className="b-val">{ausgefuehrtCount}</div>
          <div className="b-sub">
            {ausgefuehrtCount > 0
              ? t("schleuse:becken.freigabenGesamt", { count: ausgefuehrtCount })
              : t("schleuse:becken.nichtsAusgefuehrt")}
          </div>
        </button>
      </div>

      {/* ── Zustände oberhalb der Liste: Setup fehlt, GitHub getrennt, kein Space. ── */}
      {!spaceId ? (
        <div className="iio-slz-empty">
          <h3>{t("freigabe:zustand.keinSpaceTitel")}</h3>
          <p>{t("freigabe:zustand.keinSpaceText")}</p>
        </div>
      ) : (candLoading && !candData) ? (
        <div className="iio-slz-empty"><p>{t("freigabe:zustand.laedt")}</p></div>
      ) : !configured ? (
        <div className="iio-slz-empty">
          <h3>{t("freigabe:zustand.keinePipelineTitel")}</h3>
          <p>{t("freigabe:zustand.keinePipeline")}</p>
          <p style={{ marginTop: 12 }}>
            <a href="/cicd-setup" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
              {t("freigabe:zustand.zumSetup")}
            </a>
          </p>
        </div>
      ) : (
        <div className="iio-slz-list" key={basin}>

          {/* Becken „Offen": ein Kandidat je Paket, jeder einzeln freigebbar. */}
          {basin === "offen" && (
            <>
              {!kandidatVorhanden ? (
                <div className="iio-slz-empty">
                  <h3>{t("schleuse:liste.leerOffenTitel")}</h3>
                  <p>{candidate?.lastRelease?.tag
                    ? t("schleuse:liste.leerOffenSeitTag", { tag: candidate.lastRelease.tag })
                    : t("schleuse:liste.leerOffenSeitBeginn")}</p>
                </div>
              ) : (
                <section className="iio-slz-chap">
                  <div className="chap-head">
                    <span className="d">{kandidaten.length === 1
                      ? t("schleuse:eintrag.kandidat")
                      : t("schleuse:eintrag.kandidatenZahl", { count: kandidaten.length })}</span>
                    <span className="ln" />
                    <span className="s">{candidate?.lastRelease?.tag
                      ? t("schleuse:eintrag.seitTag", { tag: candidate.lastRelease.tag })
                      : t("schleuse:eintrag.seitBeginn")}</span>
                  </div>
                  {kandidaten.map((kand, ki) => {
                    const name = kand.packageName || kand.packageId || t("schleuse:blatt.keinPaket");
                    const iflowCount = kand.summary?.iflows || (kand.items || []).length;
                    return (
                      <div className="iio-slz-row" key={kand.packageId || ki}>
                        <div className="iio-slz-req iio-anim-stagger" style={ki > 0 ? { animationDelay: `${ki * 40}ms` } : undefined} onClick={() => openCandidate(kand)}>
                          <div className="req-time">
                            <div className="t">{iflowCount}</div>
                            <div className="u">{t("schleuse:eintrag.iflowsZahl", { count: iflowCount })}</div>
                          </div>
                          <span className="req-bar is-wait" />
                          <div className="req-main">
                            <div className="cat">
                              {t("schleuse:eintrag.kandidat")} · {quellStufe} → {prodEnv}
                            </div>
                            <div className="ttl">{name}</div>
                            <div className="mini-route">
                              <span className="mini-node src">{quellStufe}</span>
                              <span style={{ color: "var(--iio-text-3)" }}>→</span>
                              <span className="mini-node dst">{prodEnv}</span>
                              <span className="meta">
                                · {t("schleuse:eintrag.artefakteZahl", { count: (kand.items || []).length })}
                                {" · "}{t("schleuse:eintrag.pushesGesamt", { count: kand.summary?.pushes || 0 })}
                              </span>
                            </div>
                          </div>
                          <span className="req-chev">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {(candidate?.ohnePaket > 0 || candidate?.ohneZuordnung > 0) && (
                    <div className="iio-slz-margin">
                      {candidate.ohnePaket > 0 && (
                        <div className="sysflag"><span>{t("schleuse:liste.ohnePaket", { count: candidate.ohnePaket })}</span></div>
                      )}
                      {candidate.ohneZuordnung > 0 && (
                        <div className="sysflag"><span>{t("schleuse:liste.ohneZuordnung", { count: candidate.ohneZuordnung })}</span></div>
                      )}
                    </div>
                  )}
                </section>
              )}
            </>
          )}

          {/* Becken „Ausgeführt": Historie nach Tag gruppiert. */}
          {basin === "ausgefuehrt" && (
            <>
              {histLoading && !histData && <div className="iio-slz-empty"><p>{t("schleuse:liste.laedt")}</p></div>}
              {!histLoading && histItems.length === 0 && (
                <div className="iio-slz-empty">
                  <h3>{t("schleuse:liste.leerAusgefuehrtTitel")}</h3>
                  <p>{t("schleuse:liste.leerAusgefuehrtText", { ziel: prodEnv })}</p>
                </div>
              )}
              {!histLoading && histChapters.map(([day, items], ci) => (
                <section className="iio-slz-chap" key={day}>
                  <div className="chap-head">
                    <span className="d">{fmtDay(day, t)}</span>
                    <span className="ln" />
                    <span className="s">{t("schleuse:liste.eintrag", { count: items.length })}</span>
                  </div>
                  {items.map((r, i) => {
                    const paketName = r.packageName || r.packageId || t("schleuse:blatt.keinPaket");
                    return (
                      <div className="iio-slz-row" key={r.runId + "-" + i}>
                        <div
                          className="iio-slz-req iio-anim-stagger"
                          style={{ animationDelay: `${Math.min(i + ci * 3, 8) * 40}ms` }}
                          onClick={() => openHistory(r)}
                        >
                          <div className="req-time">
                            <div className="t">{fmtClock(r.at)}</div>
                            <div className="u">{r.approvedBy?.name || ""}</div>
                          </div>
                          <span className="req-bar is-ok" />
                          <div className="req-main">
                            <div className="cat">
                              {/* Kein Name gespeichert (Alt-Eintrag): den "von"-Teil weglassen
                                  statt "unbekannt" zu behaupten - nur zeigen, was wirklich da ist. */}
                              {r.approvedBy?.name
                                ? t("schleuse:blatt.metaFreigabe", { zeit: fmtFull(r.at), name: r.approvedBy.name, ziel: r.targetEnv || prodEnv })
                                : t("schleuse:blatt.metaFreigabeOhne", { zeit: fmtFull(r.at), ziel: r.targetEnv || prodEnv })}
                            </div>
                            <div className="ttl">{paketName}</div>
                            <div className="mini-route">
                              <span className="mini-node src">{r.sourceEnv || quellStufe}</span>
                              <span style={{ color: "var(--iio-text-3)" }}>→</span>
                              <span className="mini-node dst">{r.targetEnv || prodEnv}</span>
                              <span className="meta">
                                · {t("schleuse:eintrag.artefakteZahl", { count: (r.artifactIds || []).length })}
                                {r.tag && <> · {r.tagUrl
                                  ? <a href={r.tagUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                      style={{ color: "var(--iio-info)", textDecoration: "none" }} title={t("schleuse:blatt.tagLabel")}>{r.tag}</a>
                                  : r.tag}</>}
                                {/* Direktlink auf den Pipeline-Lauf der Freigabe: der Kern der
                                    Nachvollziehbarkeit, deshalb schon in der Zeile und nicht
                                    erst im Detail. stopPropagation: die Zeile oeffnet das Sheet. */}
                                {r.runUrl && <> · <a href={r.runUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                  style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{t("schleuse:eintrag.laufLink")}</a></>}
                              </span>
                            </div>
                          </div>
                          <span className="req-chev">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                          </span>
                        </div>
                        <div className="iio-slz-margin">
                          {r.approvalNote && (
                            <div className="flag"><span>
                              {r.approvalNote}
                              {r.approvedBy?.name && <div className="meta">{r.approvedBy.name}</div>}
                            </span></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
              {/* „Mehr laden" nur wenn Server sagt: es liegt mehr vor. */}
              {!histLoading && histTotal > histItems.length && (
                <div style={{ textAlign: "center", padding: "18px 0 6px" }}>
                  <button
                    type="button"
                    className="iio-btn iio-btn--secondary"
                    onClick={() => {
                      const next = histLimit * 2;
                      setHistLimit(next);
                      ladeHistorie(spaceId, next);
                    }}
                  >
                    {t("schleuse:liste.mehrLaden")}
                  </button>
                  <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 6 }}>
                    {t("schleuse:liste.mehrHinweis", { gezeigt: histItems.length, gesamt: histTotal })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Detail-Sheet */}
      {detail && (
        <>
          <div className="iio-slz-veil" onClick={closeSheet} />
          <aside className="iio-slz-sheet" role="dialog" aria-modal="true">
            <div className="sheet-head">
              <button type="button" className="sheet-back" onClick={closeSheet}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m14 6-6 6 6 6" /></svg>
                {t("schleuse:blatt.zurueck")}
              </button>
              <div className="sheet-title">
                <span className={"bar " + (detailIsCand ? "is-wait" : "is-ok")} />
                <h2>{detailIsHist
                  ? t("schleuse:blatt.titelHistorie", { paket: detailPaket })
                  : t("schleuse:blatt.titelKandidat", { paket: detailPaket })}</h2>
              </div>
              <div className="sheet-meta">
                {detailIsHist
                  ? (d.approvedBy?.name
                      ? t("schleuse:blatt.metaFreigabe", { zeit: fmtFull(d.at), name: d.approvedBy.name, ziel: d.targetEnv || prodEnv })
                      : t("schleuse:blatt.metaFreigabeOhne", { zeit: fmtFull(d.at), ziel: d.targetEnv || prodEnv }))
                  : t("schleuse:blatt.meta", { quelle: detailQuelle, ziel: detailZiel })}
              </div>
            </div>

            <div className="sheet-body">
              {/* Route */}
              <div className="sec iio-anim-stagger">
                <div className="sec-h">{t("schleuse:blatt.routeTitel")}</div>
                <div className="iio-slz-routemap">
                  <div className="tenant src">
                    <div className="tn">{detailQuelle}</div>
                    <div className="ts">{t("schleuse:blatt.quelle")}</div>
                  </div>
                  <span style={{ flex: "0 0 16px" }} />
                  <div className="route-fan">
                    <div className="route-line">
                      <span className="rl-stem" />
                      <span style={{ flex: "0 0 12px" }} />
                      <div className="tenant dst">
                        <div className="tn">{detailZiel}</div>
                        <div className="ts">{t("schleuse:blatt.ziel")}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Frachtbrief */}
              <div className="sec iio-anim-stagger" style={{ animationDelay: "40ms" }}>
                <div className="sec-h">{t("schleuse:blatt.inhaltTitel")}</div>
                <dl className="iio-slz-kv">
                  <dt>{t("schleuse:blatt.paketLabel")}</dt>
                  <dd>{detailPaket}</dd>
                  <dt>{t("schleuse:blatt.artefakteLabel")}</dt>
                  <dd>{detailArtefakte.length}</dd>
                  {detailPushes !== null && (
                    <>
                      <dt>{t("schleuse:blatt.pushesLabel")}</dt>
                      <dd>{detailPushes}</dd>
                    </>
                  )}
                  {detailIsHist && detailPartner.length > 0 && (
                    <>
                      <dt>{t("schleuse:blatt.partnerdirLabel")}</dt>
                      <dd>{t("schleuse:blatt.partnerdirZahl", { count: detailPartner.length })}</dd>
                    </>
                  )}
                  {detailIsHist && d.tag && (
                    <>
                      <dt>{t("schleuse:blatt.tagLabel")}</dt>
                      {/* Der Tag verlinkt auf den eingefrorenen Stand im Repo: exakt die
                          Dateien, die mit dieser Freigabe nach PROD gegangen sind. */}
                      <dd>{d.tagUrl
                        ? <a href={d.tagUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{d.tag}</a>
                        : d.tag}</dd>
                    </>
                  )}
                  {detailIsHist && (
                    <>
                      <dt>{t("schleuse:blatt.runLabel")}</dt>
                      <dd>
                        {d.runUrl
                          ? <a href={d.runUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{d.runId || t("schleuse:eintrag.kandidat")}</a>
                          : <span style={{ color: "var(--iio-text-3)" }}>{t("schleuse:blatt.runOffen")}</span>}
                      </dd>
                    </>
                  )}
                  {detailIsHist && (
                    <>
                      <dt>{t("schleuse:blatt.begruendungLabel")}</dt>
                      <dd className="note-dd">{d.approvalNote || t("schleuse:blatt.keineBegruendung")}</dd>
                    </>
                  )}
                </dl>

                {detailArtefakte.length > 0 ? (
                  <div className="iio-slz-arts">
                    {detailArtefakte.map((a, ai) => (
                      <div className="art" key={a + "-" + ai}>
                        <span className="an">{a}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="iio-slz-hint">{t("schleuse:blatt.keineArtefakte")}</div>
                )}
              </div>

              {/* Freigabe-Akt nur beim Kandidaten */}
              {detailIsCand && (
                <div className="iio-slz-approve">
                  <div className="sec-h">{t("freigabe:freigabe.titel")}</div>

                  {/* Erst blockende Zustände zeigen, DANN das Grund-Feld. Ohne Token
                      würde der POST 400 werfen – der Nutzer soll das vorher sehen. */}
                  {!tokenSet ? (
                    <div className="four-eyes" style={{ borderColor: "var(--iio-critical)" }}>
                      <span>
                        <b>{t("freigabe:zustand.githubGetrenntTitel")}</b>
                        <div style={{ marginTop: 4 }}>{t("freigabe:zustand.githubGetrennt")}</div>
                        <a href="/cicd-setup" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
                          {t("freigabe:zustand.zumSetup")}
                        </a>
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="four-eyes">
                        <span>{t("freigabe:freigabe.einleitung", { ziel: prodEnv })}</span>
                      </div>
                      <div className="signer">
                        {user?.displayName || user?.username}
                      </div>
                      <label style={{ display: "block", fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--iio-text-3)", marginTop: 8, marginBottom: 6 }}>
                        {t("freigabe:freigabe.grundLabel")}
                      </label>
                      <div className="note-field">
                        <textarea
                          value={grund}
                          onChange={e => setGrund(e.target.value)}
                          placeholder={t("freigabe:freigabe.grundPlatzhalter")}
                        />
                        <div className={"note-count" + (grund.trim().length >= 3 ? " ok" : "")}>
                          {t("freigabe:freigabe.zeichenzahl", { anzahl: grund.trim().length })}
                          {grund.trim().length >= 3 ? "" : t("freigabe:freigabe.mindestzeichen")}
                        </div>
                      </div>
                      <div className="act-row" style={{ marginTop: 14 }}>
                        <button
                          type="button"
                          className="iio-btn iio-btn--signal"
                          disabled={releasing || grund.trim().length < 3 || !detail?.data?.releasePayload}
                          onClick={freigeben}
                        >
                          {releasing ? t("freigabe:freigabe.laeuft") : t("freigabe:freigabe.knopfFreigeben", { ziel: prodEnv })}
                        </button>
                        <button type="button" className="iio-btn iio-btn--tertiary" onClick={closeSheet}>
                          {t("freigabe:freigabe.knopfSpaeter")}
                        </button>
                      </div>

                      {result && result.ok && (
                        <div className="iio-slz-confirm" style={{ borderColor: "var(--iio-success)" }}>
                          <div className="cc-t">{t("freigabe:ergebnis.erfolgTitel")}</div>
                          <div className="cc-b">
                            {t("freigabe:ergebnis.erfolgTag", { tag: result.tag || "" })}
                          </div>
                          {result.runUrl && (
                            <a href={result.runUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
                              {t("freigabe:ergebnis.erfolgLauf")}
                            </a>
                          )}
                        </div>
                      )}
                      {result && !result.ok && (
                        <div className="iio-slz-confirm" style={{ borderColor: "var(--iio-critical)" }}>
                          <div className="cc-t">{t("freigabe:ergebnis.fehlerTitel")}</div>
                          <div className="cc-b">{t("freigabe:ergebnis.fehlerHinweis", { fehler: result.error || "" })}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
